import React, { ReactElement } from 'react';
import cn from 'classnames';
import { Badge } from '@odin-labs/components';
import { kebabOrSnakeToSentenceCase } from 'utils';
import {
  PolicyConsentStatusFragment,
  formatPolicyDate,
  isPolicyAccepted,
  getPolicyDocumentUrl,
} from './helpers';

const classes = {
  row: cn('odin-p-4'),
  header: cn('odin-flex odin-items-center odin-gap-x-2'),
  name: cn('odin-font-medium odin-text-odin-primary odin-underline'),
  meta: cn('odin-text-sm odin-text-gray-500'),
};

export type PolicyConsentRowProps = {
  status: PolicyConsentStatusFragment;
  language?: string;
};

/**
 * One policy row in the re-capture gate: the document name (linked to its PDF),
 * a state badge, and a meta line showing the effective date. The accepted /
 * updated / new state is derived from the user's consent history
 * (`lastAcceptedVersion` vs the current `document.version`).
 */
export function PolicyConsentRow({
  status,
}: PolicyConsentRowProps): ReactElement {
  const { document, lastAcceptedVersion } = status;
  const accepted = isPolicyAccepted(status);
  const neverAccepted = lastAcceptedVersion == null;

  let badge: { text: string; color: 'green' | 'yellow' };
  if (accepted) {
    badge = { text: 'Already accepted', color: 'green' };
  } else if (neverAccepted) {
    badge = { text: 'New for you', color: 'yellow' };
  } else {
    badge = { text: 'Updated', color: 'yellow' };
  }

  return (
    <div className={classes.row}>
      <div className={classes.header}>
        <a
          href={getPolicyDocumentUrl(document.key)}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.name}
        >
          {kebabOrSnakeToSentenceCase(document.key)}
        </a>
        <Badge size="md" type="basic" color={badge.color} text={badge.text} />
      </div>
      <div className={classes.meta}>
        Effective {formatPolicyDate(document.effectiveAt)}
      </div>
    </div>
  );
}
